import {
    filterTemplatesByName,
    groupTemplatesByMode,
    templateModeClass,
    templateModeLabel,
} from '../../core/client/template-modes.core.js';
import {escapeHtml} from '../../core/escape-html.js';

/**
 * @param {import('../../shell/types/client.types.js').ClientTemplateSummary} template
 * @returns {string} inner markup of one `.template-item` in the template list
 */
function templateItemMarkup(template) {
    const name = escapeHtml(template.name);
    return `
                <div class="template-info">
                    <i aria-hidden="true" class="fa-solid fa-file-code"></i>
                    <div class="template-text">
                        <span class="template-name">${name}</span>
                        <span class="template-mode template-mode--${templateModeClass(template.mode)}">${escapeHtml(templateModeLabel(template.mode))}</span>
                    </div>
                </div>
                <div class="template-actions">
                    <button type="button" class="btn btn-outline btn-sm btn-edit" data-name="${name}" aria-label="Edit template ${name}">
                        <i aria-hidden="true" class="fa-solid fa-pen-to-square"></i> Edit
                    </button>
                    <button type="button" class="btn btn-delete btn-sm btn-remove" data-name="${name}" aria-label="Delete template ${name}">
                        <i aria-hidden="true" class="fa-solid fa-trash-can"></i> Delete
                    </button>
                </div>
            `;
}

/**
 * @param {string} name
 * @param {string} query text typed in the template filter
 * @returns {boolean} true when the filter keeps the template named `name` in the list
 */
export function templateMatchesFilter(name, query) {
    return filterTemplatesByName([{name}], query).length > 0;
}

/**
 * Renders the template list into `container`, replacing its content: an empty state when the
 * client has no templates, a message when `query` matches none, and otherwise one titled group per
 * render mode. The template named `editingName` is marked as the one open in the form.
 *
 * @param {HTMLElement} container
 * @param {import('../../shell/types/client.types.js').ClientTemplateSummary[]} templates
 * @param {{query?: string, editingName?: string | null}} [options]
 */
export function renderTemplateList(container, templates, {query = '', editingName = null} = {}) {
    container.innerHTML = '';

    if (templates.length === 0) {
        const empty = document.createElement('empty-state');
        const title = document.createElement('span');
        title.slot = 'title';
        title.textContent = 'No templates yet';
        empty.append(title, 'Add the first one with the form below.');
        container.appendChild(empty);
        return;
    }

    const matches = filterTemplatesByName(templates, query);
    if (matches.length === 0) {
        const message = document.createElement('p');
        message.className = 'template-list-message';
        message.textContent = `No templates match "${query.trim()}".`;
        container.appendChild(message);
        return;
    }

    for (const group of groupTemplatesByMode(matches)) {
        const heading = document.createElement('h4');
        heading.className = 'template-group-title';
        heading.textContent = `${group.label} (${group.templates.length})`;
        container.appendChild(heading);

        for (const template of group.templates) {
            const item = document.createElement('div');
            item.className = 'template-item';
            item.innerHTML = templateItemMarkup(template);
            if (template.name === editingName) {
                item.classList.add('is-editing');
                item.setAttribute('aria-current', 'true');
            }
            container.appendChild(item);
        }
    }
}
